import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';

import { buildCreateQuoteBreadcrumb } from './CreateQuote.utils';
import GroupCoverageSection from './sections/GroupCoverageSection/GroupCoverageSection';
import NoProductsAvailableSection from './sections/NoProductsAvailableSection/NoProductsAvailableSection';
import PlanSelectionSection from './sections/PlanSelectionSection/PlanSelectionSection';
import ProductsSection from './sections/ProductsSection/ProductsSection';
import QuoteSummaryPanel from './sections/QuoteSummaryPanel/QuoteSummaryPanel';
import { useCreateQuoteData } from './useCreateQuoteData';
import { useCreateQuoteFlags } from './useCreateQuoteFlags';
import CustomButton from '../../../shared/components/Buttons/CustomButton/CustomButton';
import SecondaryHeader from '../../../shared/components/Globals/Headers/SecondaryHeader/SecondaryHeader';
import LoadingFullScreen from '../../../shared/components/Globals/LoadingFullScreen/LoadingFullScreen';
import CustomModal from '../../../shared/components/Modals/CustomModal/CustomModal';
import { PROSPECTS_ROUTE } from '../../../shared/constants/auth';
import { MIN_EMPLOYEES_FOR_APP_ONLY, MIN_EMPLOYEES_FOR_QUOTES } from '../../../shared/constants/quotes';
import {
  CLICK_ACTION,
  KEEP_EDITING,
  LEAVE_WITHOUT_SAVING,
  QUOTATION_BACK,
  SAVE_QUOTE,
  SCREEN_FULL_PROFILE,
  SCREEN_PROSPECT_TABLE,
  SELECT_PRODUCT,
  SELECT_PRODUCT_OPTION,
  SELECT_TOBACCO_STATUS,
  UPDATE_QUOTE
} from '../../../shared/constants/tagManager';
import { useBroker } from '../../../shared/hooks/useBroker/useBroker';
import type { IQuoteFlowReturnTarget } from '../../../shared/hooks/useQuoteActions/useQuoteActions';
import { useQuotePriceCalculator } from '../../../shared/hooks/useQuotePriceCalculator/useQuotePriceCalculator';
import { useUser } from '../../../shared/hooks/useUser/useUser';
import type { IQuickCensusAgeSummaryEntry, IQuickCensusFileResponse, IQuote } from '../../../shared/interfaces/quotes';
import { Quotes } from '../../../shared/services/api/quotes/quotes';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_QUOTES } from '../../../shared/translations/quotes';
import type { AgeInputMode, TobaccoStatus } from '../../../shared/types/quotes';
import { isAppOnly } from '../../../shared/utils/prospects/prospects';
import {
  buildBundleSummaryLine,
  buildInitialCalculatedPrice,
  buildPriceCalculationRequest,
  buildSaveQuotePayload,
  buildSummary,
  checkHasNullPayments,
  filterBundlesBySalesModel,
  getAgeValid
} from '../../../shared/utils/quotes/quotes';
import './CreateQuote.scss';

const CreateQuote = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { prospectId: prospectIdParam, quoteId: quoteIdParam } = useParams<{ prospectId: string; quoteId: string }>();
  const prospectId = Number(prospectIdParam);
  const parsedQuoteId = quoteIdParam ? Number(quoteIdParam) : null;
  const quoteId = parsedQuoteId != null && !Number.isNaN(parsedQuoteId) ? parsedQuoteId : null;
  const isEditing = quoteId != null;
  const editingQuote = (location.state as { quote?: IQuote } | null)?.quote ?? null;
  const returnTo = (location.state as { returnTo?: IQuoteFlowReturnTarget } | null)?.returnTo ?? null;
  const fromClient = returnTo !== null;
  const { brokerId, userInfo, loading: userLoading } = useUser();
  const { commercialAddons, loadingAddons, commercialPlans, loadingPlans } = useBroker();

  const {
    prospect,
    loading,
    groups,
    employees,
    setEmployees,
    averageAge,
    setAverageAge,
    quoteName,
    setQuoteName,
    segment,
    setSegment,
    selection,
    setSelection,
    censusSummaries,
    setCensusSummaries,
    censusFile,
    setCensusFile,
    initialPriceFactorsStr
  } = useCreateQuoteData({
    brokerId,
    userLoading,
    loadingAddons,
    loadingPlans,
    commercialAddons,
    prospectId,
    isEditing,
    editingQuote
  });

  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(
    () => editingQuote?.commercialAddons.find((addon) => addon.entity === 'plan')?.commercialAddonId ?? null
  );
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState(PROSPECTS_ROUTE);
  const employeesBeforeCensusRef = useRef<string | null>(null);

  const employeesNum = Number.parseInt(employees, 10) || 0;
  const ageNum = Number.parseInt(averageAge, 10) || 0;
  const appOnly = isAppOnly(prospect);
  const minEmployees = appOnly ? MIN_EMPLOYEES_FOR_APP_ONLY : MIN_EMPLOYEES_FOR_QUOTES;
  const availableBundles = useMemo(
    () => filterBundlesBySalesModel(commercialPlans, appOnly),
    [commercialPlans, appOnly]
  );

  const activeGroups = useMemo(() => (appOnly ? [] : groups), [appOnly, groups]);
  const activeSelection = useMemo(() => (appOnly ? {} : selection), [appOnly, selection]);

  const priceCalculatorRequest = useMemo(
    () => buildPriceCalculationRequest(activeGroups, activeSelection, employeesNum, ageNum, censusSummaries),
    [activeGroups, activeSelection, employeesNum, ageNum, censusSummaries]
  );

  const initialCalculatedPrice = useMemo(
    () => buildInitialCalculatedPrice(isEditing, editingQuote),
    [isEditing, editingQuote]
  );

  const hasNullPayments = checkHasNullPayments(isEditing, editingQuote);
  const currentPriceFactorsStr = JSON.stringify({ selection, employees: employeesNum, age: ageNum, censusSummaries });
  const skipRecalculation =
    isEditing &&
    !hasNullPayments &&
    initialPriceFactorsStr !== null &&
    currentPriceFactorsStr === initialPriceFactorsStr;

  const { price: calculatedPriceFromApi } = useQuotePriceCalculator(priceCalculatorRequest, skipRecalculation);

  const calculatedPrice = skipRecalculation ? initialCalculatedPrice : calculatedPriceFromApi;

  const ageValid = getAgeValid(ageNum, activeGroups, activeSelection);

  const selectedBundle = availableBundles.find((bundle) => bundle.id === selectedBundleId) ?? null;
  const singleBundleLocked = availableBundles.length === 1;

  useEffect(() => {
    if (singleBundleLocked && selectedBundleId !== availableBundles[0].id) {
      setSelectedBundleId(availableBundles[0].id);
    }
  }, [singleBundleLocked, availableBundles, selectedBundleId]);

  const summary = useMemo(() => {
    const base = buildSummary(activeGroups, activeSelection, employeesNum, ageNum, calculatedPrice, censusSummaries);
    if (!selectedBundle) return base;
    const bundleLine = buildBundleSummaryLine(selectedBundle, employeesNum);
    const employerTotal = base.employerTotal + bundleLine.total;
    return {
      ...base,
      employerLines: [bundleLine, ...base.employerLines],
      employerTotal,
      grandTotal: employerTotal
    };
  }, [activeGroups, activeSelection, employeesNum, ageNum, calculatedPrice, censusSummaries, selectedBundle]);

  const {
    employerPaidSelectedCount,
    voluntarySelectedCount,
    hasEmployerPaidGroups,
    isEditDataMissing,
    offerEmpty,
    addonsDisabled,
    addonsBlockedByPlan,
    employeesError,
    averageAgeError,
    canSave,
    saveTooltip
  } = useCreateQuoteFlags({
    groups: activeGroups,
    selection: activeSelection,
    employees,
    employeesNum,
    averageAge,
    ageValid,
    quoteName,
    saving,
    isEditing,
    editingQuote,
    commercialPlansCount: availableBundles.length,
    hasSelectedBundle: selectedBundle !== null,
    hasMissingRate: summary.hasMissingRate,
    requiresProductSelection: !appOnly,
    minEmployees,
    t
  });

  const handleRequestLeave = (target: string = PROSPECTS_ROUTE) => {
    const gtmParam = target === PROSPECTS_ROUTE ? SCREEN_PROSPECT_TABLE : SCREEN_FULL_PROFILE;
    TagManagerService.pushEvent(CLICK_ACTION, QUOTATION_BACK, gtmParam);

    setLeaveTarget(target);
    setShowLeaveModal(true);
  };

  const handleKeepEditing = () => {
    TagManagerService.pushEvent(CLICK_ACTION, KEEP_EDITING);
    setShowLeaveModal(false);
  };

  const handleConfirmLeave = () => {
    TagManagerService.pushEvent(CLICK_ACTION, LEAVE_WITHOUT_SAVING);
    setShowLeaveModal(false);
    if (returnTo?.path === leaveTarget) {
      navigate(leaveTarget, { state: returnTo.state });
    } else {
      navigate(leaveTarget);
    }
  };

  const handleToggle = useCallback(
    (key: string) => {
      const isCurrentlySelected = selection[key]?.selected ?? false;
      const productName = groups.find((g) => g.key === key)?.name ?? key;
      TagManagerService.pushEvent(CLICK_ACTION, SELECT_PRODUCT, {
        product: productName,
        selected: !isCurrentlySelected
      });
      setSelection((prev) => ({ ...prev, [key]: { ...prev[key], selected: !prev[key].selected } }));
    },
    [groups, selection, setSelection]
  );

  const handleSelectAddon = useCallback(
    (key: string, addonId: number) => {
      const group = groups.find((g) => g.key === key);
      const addonName = group?.addons.find((a) => a.id === addonId)?.name ?? String(addonId);
      TagManagerService.pushEvent(CLICK_ACTION, SELECT_PRODUCT_OPTION, {
        product: group?.name ?? key,
        option: addonName
      });
      setSelection((prev) => ({ ...prev, [key]: { ...prev[key], addonId } }));
    },
    [groups, setSelection]
  );

  const handleTobacco = useCallback(
    (key: string, status: TobaccoStatus) => {
      setSelection((prev) => ({ ...prev, [key]: { ...prev[key], tobaccoStatus: status } }));
      TagManagerService.pushEvent(CLICK_ACTION, SELECT_TOBACCO_STATUS, { status: status });
    },
    [setSelection]
  );

  const handleAgeInputModeChange = useCallback(
    (key: string, mode: AgeInputMode) => {
      setSelection((prev) => {
        const current = prev[key]?.ageInputMode;
        if (current === mode) return prev;
        return { ...prev, [key]: { ...prev[key], ageInputMode: mode } };
      });
      if (mode === 'census') {
        const distribution = censusFile?.fileJson?.distribution;
        if (distribution) {
          setCensusSummaries((prev) => {
            if (key in prev) return prev;
            return { ...prev, [key]: distribution };
          });
        }
      } else if (mode === 'average') {
        setCensusSummaries((prev) => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [censusFile, setSelection, setCensusSummaries]
  );

  const handleCensusApply = useCallback(
    (
      key: string,
      _avgAge: number,
      summaryEntries: IQuickCensusAgeSummaryEntry[],
      fileObj?: IQuickCensusFileResponse,
      fileName?: string,
      rowCount?: number
    ) => {
      setCensusSummaries((prev) => ({ ...prev, [key]: summaryEntries }));
      if (fileObj) {
        setCensusFile({
          id: fileObj.id,
          fileJson: { distribution: summaryEntries },
          fileName,
          rowCount
        });
      }
    },
    [setCensusSummaries, setCensusFile]
  );

  const handleCensusRemove = useCallback(
    (_key: string) => {
      setCensusFile(null);
      setCensusSummaries({});
      if (employeesBeforeCensusRef.current !== null) {
        setEmployees(employeesBeforeCensusRef.current);
        employeesBeforeCensusRef.current = null;
      }
    },
    [setCensusFile, setCensusSummaries, setEmployees]
  );

  const handleSelectBundle = useCallback((bundleId: number) => {
    setSelectedBundleId(bundleId);
  }, []);

  const handleSave = async () => {
    if (isEditDataMissing) {
      setSubmitError(t(TRANSLATIONS_QUOTES.create.notFound));
      return;
    }
    if (!canSave || !brokerId || !prospect) return;
    setSubmitError(null);
    setSaving(true);

    const body = buildSaveQuotePayload({
      isEditing,
      editingQuote,
      userInfo,
      quoteName,
      segment,
      groups: activeGroups,
      selection: activeSelection,
      employees: employeesNum,
      age: ageNum,
      summary,
      censusFile,
      selectedBundle
    });

    try {
      let savedQuoteId = quoteId;

      if (quoteId === null) {
        const response = await Quotes.createQuote(brokerId, prospect.id, body);
        savedQuoteId = response.id;
        TagManagerService.pushEvent(CLICK_ACTION, SAVE_QUOTE);
      } else {
        await Quotes.updateQuote(brokerId, prospect.id, quoteId, body);
        TagManagerService.pushEvent(CLICK_ACTION, UPDATE_QUOTE);
      }

      navigate(fromClient && returnTo ? returnTo.path : PROSPECTS_ROUTE, {
        state: {
          ...(fromClient && returnTo ? returnTo.state : {}),
          quoteEdited: {
            quoteId: savedQuoteId,
            quoteName: quoteName.trim(),
            companyName: prospect.companyName,
            prospectId: prospect.id,
            action: quoteId === null ? 'create' : 'update',
            prospectStatus: prospect.status
          }
        }
      });
    } catch {
      setSubmitError(t(TRANSLATIONS_QUOTES.create.toast.saveError));
    } finally {
      setSaving(false);
    }
  };

  const employerPaidStepNumber = availableBundles.length > 1 ? 3 : 2;
  const voluntaryStepNumber = employerPaidStepNumber + (hasEmployerPaidGroups ? 1 : 0);

  const breadcrumbItems = buildCreateQuoteBreadcrumb({
    prospect,
    isEditing,
    fromClient,
    returnTo,
    t,
    onRequestLeave: handleRequestLeave
  });

  if (loading || userLoading) {
    return <LoadingFullScreen show />;
  }

  if (!prospect) {
    return (
      <div className="create-quote">
        <SecondaryHeader breadcrumbItems={breadcrumbItems} />
        <div className="create-quote__not-found">{t(TRANSLATIONS_QUOTES.create.notFound)}</div>
      </div>
    );
  }

  return (
    <div className="create-quote">
      <SecondaryHeader breadcrumbItems={breadcrumbItems} />

      <div className="create-quote__body">
        <div className="create-quote__main">
          <h1 className="create-quote__heading">
            {(isEditing ? t(TRANSLATIONS_QUOTES.create.editQuote) : t(TRANSLATIONS_QUOTES.create.newQuote)) +
              ` — ${prospect.companyName}`}
          </h1>

          <GroupCoverageSection
            employees={employees}
            employeesError={employeesError}
            employeesComplete={employeesNum >= minEmployees}
            quoteName={quoteName}
            segment={segment}
            onEmployeesChange={setEmployees}
            onQuoteNameChange={setQuoteName}
            onSegmentChange={setSegment}
            minEmployees={minEmployees}
          />

          {offerEmpty ? (
            <NoProductsAvailableSection />
          ) : (
            <>
              {availableBundles.length > 0 && (
                <PlanSelectionSection
                  bundles={availableBundles}
                  selectedBundleId={selectedBundleId}
                  disabled={employeesNum < minEmployees}
                  locked={singleBundleLocked}
                  onSelect={handleSelectBundle}
                />
              )}

              {!appOnly && (
                <>
                  <ProductsSection
                    variant="employerPaid"
                    number={String(employerPaidStepNumber)}
                    groups={groups}
                    selection={selection}
                    selectedCount={employerPaidSelectedCount}
                    age={ageNum}
                    averageAge={averageAge}
                    averageAgeError={averageAgeError}
                    disabled={addonsDisabled}
                    blockedByPlan={addonsBlockedByPlan}
                    onToggle={handleToggle}
                    onSelectAddon={handleSelectAddon}
                    onTobacco={handleTobacco}
                    onAverageAgeChange={setAverageAge}
                    onAgeInputModeChange={handleAgeInputModeChange}
                    employeesSize={employeesNum}
                    onCensusApply={handleCensusApply}
                    onCensusRemove={handleCensusRemove}
                    onUpdateHeadcount={(next) => {
                      employeesBeforeCensusRef.current = employees;
                      setEmployees(String(next));
                    }}
                    summary={summary}
                    censusFile={censusFile}
                  />

                  <ProductsSection
                    variant="voluntary"
                    number={String(voluntaryStepNumber)}
                    groups={groups}
                    selection={selection}
                    selectedCount={voluntarySelectedCount}
                    age={ageNum}
                    averageAge={averageAge}
                    averageAgeError={averageAgeError}
                    disabled={addonsDisabled}
                    blockedByPlan={addonsBlockedByPlan}
                    onToggle={handleToggle}
                    onSelectAddon={handleSelectAddon}
                    onTobacco={handleTobacco}
                    onAverageAgeChange={setAverageAge}
                    onAgeInputModeChange={handleAgeInputModeChange}
                    employeesSize={employeesNum}
                    onCensusApply={handleCensusApply}
                    onCensusRemove={handleCensusRemove}
                    onUpdateHeadcount={(next) => {
                      employeesBeforeCensusRef.current = employees;
                      setEmployees(String(next));
                    }}
                    summary={summary}
                    censusFile={censusFile}
                  />
                </>
              )}
            </>
          )}
        </div>

        <QuoteSummaryPanel
          prospectName={prospect.companyName}
          summary={summary}
          employees={employeesNum}
          isEditing={isEditing}
          canSave={canSave}
          saving={saving}
          saveTooltip={saveTooltip}
          submitError={submitError}
          onSave={handleSave}
        />
      </div>

      <CustomModal
        open={showLeaveModal}
        onClose={handleKeepEditing}
        onBackdropClick={handleKeepEditing}
        title={t(TRANSLATIONS_QUOTES.create.leaveModal.title)}
        size="s"
        customClass="create-quote__leave-modal"
      >
        <p className="create-quote__leave-modal-text">{t(TRANSLATIONS_QUOTES.create.leaveModal.description)}</p>
        <div className="create-quote__leave-modal-actions">
          <CustomButton variant="default" size="l" onClick={handleKeepEditing}>
            {t(TRANSLATIONS_QUOTES.create.leaveModal.keepEditing)}
          </CustomButton>
          <CustomButton variant="lost" size="l" onClick={handleConfirmLeave}>
            {t(TRANSLATIONS_QUOTES.create.leaveModal.leaveAnyway)}
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
};

export default CreateQuote;
